<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { NSpin } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import { useAgentHostStatsStore } from '../stores/agent_host_stats'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { formatBytes } from '../composables/formatBytes'

// Read-only droplet-wide monitoring page (#860) — a sibling to
// AdminAgentRepos.vue's per-repo devcontainer status, but scoped to the
// host VM itself: memory, disk, uptime, load average, and how many
// devcontainers are currently running across all repos. No actions here —
// see PRD #859's explicit read-only decision. CPU % ships separately (#861)
// once its cached two-sample delta is in place.
const store = useAgentHostStatsStore()
useDocumentTitle('Host monitoring')

// Matches AdminAgentRepos.vue's own polling cadence (#842) — an
// occasionally-checked ops page, not something watched continuously.
const STATUS_POLL_MS = 15_000
let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(async () => {
  try {
    await store.fetchStats()
  } catch {
    /* loadError surfaces in template */
  }
  pollTimer = setInterval(() => {
    void store.fetchStats().catch(() => {})
  }, STATUS_POLL_MS)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const memoryLabel = computed(() => {
  const s = store.stats
  if (!s || s.memory_used_bytes == null || s.memory_total_bytes == null) return '—'
  return `${formatBytes(s.memory_used_bytes)} / ${formatBytes(s.memory_total_bytes)}`
})

const diskLabel = computed(() => {
  const s = store.stats
  if (!s || s.disk_used_bytes == null || s.disk_total_bytes == null) return '—'
  return `${formatBytes(s.disk_used_bytes)} / ${formatBytes(s.disk_total_bytes)}`
})

// "3d 4h 12m" — hours only appear once days do (or are non-zero), minutes
// always show so a fresh-boot droplet still reads as something ("0m") not
// a blank string.
function formatUptime(seconds: number | null): string {
  if (seconds == null) return '—'
  const totalMinutes = Math.floor(seconds / 60)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  const parts: string[] = []
  if (days) parts.push(`${days}d`)
  if (days || hours) parts.push(`${hours}h`)
  parts.push(`${minutes}m`)
  return parts.join(' ')
}

const uptimeLabel = computed(() => formatUptime(store.stats?.uptime_seconds ?? null))

const loadAverageLabel = computed(() => {
  const s = store.stats
  if (!s || s.load_average_1m == null || s.load_average_5m == null || s.load_average_15m == null) return '—'
  const fmt = (n: number) => n.toFixed(2)
  return `${fmt(s.load_average_1m)} / ${fmt(s.load_average_5m)} / ${fmt(s.load_average_15m)}`
})

const runningCountLabel = computed(() => store.stats?.running_devcontainer_count ?? '—')

// null on the very first poll after an agent-box restart (no prior sample
// yet to compute a delta against, #861) — rendered the same as every other
// unavailable metric rather than a misleading 0%.
const cpuLabel = computed(() => {
  const cpu = store.stats?.cpu_percent
  return cpu == null ? '—' : `${cpu.toFixed(1)}%`
})
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Host monitoring</h1>
        <p class="text-sm text-white/60 m-0">
          Read-only droplet-wide health for the agent box VM that every repo's devcontainer runs on. Refreshes
          automatically every {{ STATUS_POLL_MS / 1000 }}s.
        </p>
      </header>

      <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="agent-host-stats-load-error">
        Failed to load host stats: {{ store.loadError }}
      </div>

      <dl v-else class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm rounded border border-white/10 bg-black/20 p-4 m-0">
        <dt class="text-white/40">CPU</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-cpu">{{ cpuLabel }}</dd>
        <dt class="text-white/40">Memory (used / total)</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-memory">{{ memoryLabel }}</dd>
        <dt class="text-white/40">Disk (used / total)</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-disk">{{ diskLabel }}</dd>
        <dt class="text-white/40">Uptime</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-uptime">{{ uptimeLabel }}</dd>
        <dt class="text-white/40">Load average (1m / 5m / 15m)</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-load-average">
          {{ loadAverageLabel }}
        </dd>
        <dt class="text-white/40">Running devcontainers</dt>
        <dd class="m-0 text-white/70 tabular-nums" data-testid="agent-host-stats-running-count">
          {{ runningCountLabel }}
        </dd>
      </dl>
    </section>
  </RouteShell>
</template>
